import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import cx from 'classnames';
import _ from 'lodash';

import AssetIcon from 'src/components/asset/icon';
import Text from 'src/components/text';
import Thumbnail from 'src/components/thumbnail';
import Collapse from 'src/pages/asset/components/animation/collapse';
import { FileService } from 'src/utils/file.service';

import { ChatStepTypeToRender, DealAgentChat } from 'src/types/dealAgent';
import styles from './styles.module.scss';
import { responseEventTypes } from 'src/utils/dealAgent.helper';

const LOADING_MESSAGES = [
    'Rummaging through the archives to unravel the mysteries of the universe...just a few more moments!',
    'Searching for answers in all documents, so you don\'t have to go full detective mode!',
    'Roses are red, violets are blue, searching for answers, just for you!',
    'Digging through the digital haystack, to find the needle of knowledge you seek!',
    'Putting on my detective hat and scouring the documents for clues to solve the mystery!',
];

const MAX_VISIBLE_ASSETS = 9;
interface DealAgentChatAnswerProps {
    index: number;
    chat: any;
    skipTypewriting: boolean;
    showDivider?: boolean;
}

const ChatAnswer: React.FC<DealAgentChatAnswerProps> = ({
    index,
    chat,
    skipTypewriting,
    showDivider = true,
}) => {
    const [showAssets, setShowAssets] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
    const answerContainerRef = useRef<HTMLDivElement>(null);

    const events = chat.events || []; // get the events
    const errorWhileStreaming = events.some((e: any) => !!e?.error); // if there is an error, show the response step

    // const [hoveredCite, setHoveredCite] = useState<{
    //     citeIndex: number;
    //     targetEl: HTMLElement;
    // } | null>(null);
    // const citeTooltipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    //  const citeListenersCleanupRef = useRef<(() => void) | null>(null);

    // Citations
    // const contents = useMemo(() => {
    //     return [...(chat.citations || [])]
    //         .sort((a: any, b: any) => (a.page || 0) - (b.page || 0))
    //         .map((c: any) => c.content);
    // }, [chat.citations]);

    // Follow Up questionns
    // const followUpQuestions = useMemo(() => {
    //     const events = chat.events || [];
    //     const followupEvents = events.filter((e: any) => e.type === 'follow_up');
    //     if (followupEvents.length === 0) return [];
    //     const sorted = [...followupEvents].sort((a, b) => (b.time || 0) - (a.time || 0));
    //     const latest = sorted[0];
    //     const questions = (latest?.payload?.follow_up_questions || []).map((q: any) => q.question);
    //     return Array.isArray(questions) ? questions : [];
    // }, [chat.events]);

    const remarkPlugins = useMemo(() => [remarkGfm], []);
    const rehypePlugins = useMemo(() => [rehypeRaw], []);

    const chatError = chat?.error;
    const answerErrorMessage = chatError?.message ? String(chatError.message) : "";

    useEffect(() => {
        if (answerErrorMessage || errorWhileStreaming) { // if there is an error, stop loading
            setLoadingMessage(null);
            return;
        }
        if (chat.creating && !loadingMessage) {
            setLoadingMessage(
                LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)],
            );
        } else if (!chat.creating) {
            setLoadingMessage(null);
        }
    }, [chat.creating, loadingMessage, answerErrorMessage, errorWhileStreaming]);

    useEffect(() => {
        if (skipTypewriting) setShowAssets(true);
    }, [skipTypewriting]);



    return (
        <div key={index} className={styles.chatContainer}>
            <div className={styles.questionContainer}>
                <p className={styles.question}>{chat.question}</p>
            </div>

            {/* Planning section */}
            {chat.showPlan && (
                <ReasoningProcess
                    chat={chat}
                    defaultExpanded={chat.creating}
                />
            )}

            {/* Answer */}
            {chat.answer && (
                <div className={styles.answerContainer} ref={answerContainerRef}>
                    <div className={cx(styles.answer, styles.markdown)}>
                        <Markdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins}>
                            {chat.answer}
                        </Markdown>
                    </div>
                </div>
            )}

            {/* Error */}
            {(answerErrorMessage || errorWhileStreaming) && (
                <div className={styles.answerContainer} ref={answerContainerRef}>
                    <div className={cx(styles.answer, styles.markdown)}>
                        <Markdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins}>
                            We couldn't generate an answer. Please try again.
                        </Markdown>
                    </div>
                </div>
            )}

            {/* Divider */}
            {showDivider && <div className={styles.divider} />}
        </div>
    );
};

// ReasoningProcess
interface ReasoningProcessProps {
    chat: DealAgentChat;
    defaultExpanded: boolean;
}

const ReasoningProcess: React.FC<ReasoningProcessProps> = ({ chat, defaultExpanded = true }) => {
    const events = chat.events || [];
    const errorWhileStreaming = events.some((e: any) => !!e?.error); // if there is an error, show the response step

    const [expanded, setExpanded] = useState(defaultExpanded);

    const orderedStepTypes = useMemo(() => {
        const sorted = [...events].sort((a, b) => (a.time || 0) - (b.time || 0));
        const seen = new Set<string>();
        const result: ChatStepTypeToRender[] = [];
        for (const e of sorted) {
            if (e?.error && !seen.has('response')) { // if there is an error, show the response step
                seen.add('response');
                result.push('response');
                continue;
            }
            const t = e.type;
            if (!t) continue;
            if (t === 'thought' && !seen.has('thought')) {
                seen.add('thought');
                result.push('thought');
            } else if (t === 'search_asset' && !seen.has('search_asset')) {
                seen.add('search_asset');
                result.push('search_asset');
            } else if (responseEventTypes.includes(t) && !seen.has('response')) {
                seen.add('response');
                result.push('response');
            }
        }
        return result;
    }, [events]);

    const sortedEvents = useMemo(
        () => [...events].sort((a, b) => (a.time || 0) - (b.time || 0)),
        [events],
    );

    const eventsByType = useMemo(() => {
        const map: Record<string, any[]> = {};
        for (const e of sortedEvents) {
            if (!map[e.type]) map[e.type] = [];
            map[e.type].push(e);
        }
        return map;
    }, [sortedEvents]);

    const hasFinalAnswer = useMemo(
        () => errorWhileStreaming || (eventsByType['final_answer']?.length ?? 0) > 0,
        [eventsByType, errorWhileStreaming],
    );

    const hasActiveThoughts = useMemo(() => { // has active thoughts means response is not generated yet & thinking is in progress
        const thoughts = eventsByType['thought'] || [];
        return thoughts.length > 0 && chat.creating && !errorWhileStreaming;
    }, [eventsByType, chat.creating]);

    const latestResponseStepType = useMemo<'answer' | 'critique_answer' | null>(() => {
        if (hasFinalAnswer) return null;
        const answerEvents = eventsByType['answer'] || [];
        const critiqueEvents = eventsByType['critique_answer'] || [];
        const maxAnswerTime = answerEvents.length ? Math.max(...answerEvents.map(e => e.time || 0)) : 0;
        const maxCritiqueTime = critiqueEvents.length ? Math.max(...critiqueEvents.map(e => e.time || 0)) : 0;
        if (maxAnswerTime >= maxCritiqueTime && answerEvents.length > 0) return 'answer';
        if (critiqueEvents.length > 0) return 'critique_answer';
        return null;
    }, [eventsByType, hasFinalAnswer]);

    useEffect(() => {
        setExpanded(defaultExpanded);
    }, [defaultExpanded]);

    const displayedStepsCount = useMemo(() => {
        let count = 0;
        for (const stepType of orderedStepTypes) {
            if (stepType === 'thought' && (eventsByType['thought']?.length ?? 0) > 0) count += 1;
            else if (stepType === 'search_asset' && (eventsByType['search_asset']?.length ?? 0) > 0) count += 1;
            else if (stepType === 'response' && (hasFinalAnswer || latestResponseStepType !== null)) count += 1;
        }
        return count;
    }, [orderedStepTypes, eventsByType, hasFinalAnswer, latestResponseStepType]);

    const headerTitle =
        hasFinalAnswer && displayedStepsCount > 0
            ? `${displayedStepsCount} steps completed`
            : 'Thinking through...';

    const renderStep = useCallback(
        (stepType: ChatStepTypeToRender) => {
            switch (stepType) {
                case 'thought': {
                    const steps = eventsByType['thought'] || [];
                    if (steps.length === 0) return null;
                    return (
                        <>
                            <ReasoningThoughtStep step={steps[steps.length - 1]} label="Thinking through your question" />
                            {hasActiveThoughts && <ReasoningAnswerStep // show only when thought is completed & response is not generated
                                steps={[{ completed: false }]}
                                label="Crafting your response"
                            />}
                        </>
                    );
                }
                case 'search_asset': {
                    const steps = eventsByType['search_asset'] || [];
                    if (steps.length === 0) return null;
                    return (
                        <ReasoningSearchAssetsStep steps={steps} label="Searching relevant insights" />
                    );
                }
                case 'response':
                    if (hasFinalAnswer) return <ReasoningResponseGeneratedStep />;
                    if (latestResponseStepType === 'answer')
                        return (
                            <ReasoningAnswerStep
                                steps={eventsByType['answer'] || []}
                                label="Crafting your response"
                            />
                        );
                    if (latestResponseStepType === 'critique_answer')
                        return (
                            <ReasoningCritiqueStep
                                steps={eventsByType['critique_answer'] || []}
                                label="Reviewing your response"
                            />
                        );
                    return null;
                default:
                    return null;
            }
        },
        [eventsByType, hasFinalAnswer, latestResponseStepType],
    );

    return (
        <div
            className={cx(styles.thoughtProcess, {
                [styles.expanded]: expanded,
                [styles.hasFinalAnswer]: hasFinalAnswer,
            })}
        >
            <div className={styles.header} onClick={() => setExpanded(!expanded)}>
                <p className={cx(styles.title, { [styles.blink]: !hasFinalAnswer })}>{headerTitle}</p>
                {hasFinalAnswer && (
                    <span className={cx(styles.headerChevron, 'paperflite-dropdown')} />
                )}
                <div className={styles.separator} />
            </div>
            <Collapse isOpen={expanded}>
                <div className={styles.content}>
                    {events.length > 0 &&
                        orderedStepTypes.map(stepType => (
                            <React.Fragment key={stepType}>{renderStep(stepType)}</React.Fragment>
                        ))}
                </div>
            </Collapse>
        </div>
    );
};

// Reasoning sub-steps
interface ReasoningThoughtStepProps {
    step: any;
    label: string;
}

const ReasoningThoughtStep: React.FC<ReasoningThoughtStepProps> = ({ step, label }) => (
    <div className={cx(styles.reasoningStep, styles.thoughtStep)}>
        <div className={styles.left}>
            <div className={styles.stepDot} />
            <div className={styles.line} />
        </div>
        <div className={styles.right}>
            <p className={styles.stepTitle}>{label}</p>
            <p className={styles.stepContent}>{step.payload}</p>
        </div>
    </div>
);

// Reasoning Search Assets Step
interface ReasoningSearchAssetsStepProps {
    steps: any[];
    label: string;
}

const ReasoningSearchAssetsStep: React.FC<ReasoningSearchAssetsStepProps> = ({ steps, label }) => {
    const assets = useMemo(() => {
        const seen = new Set<string>();
        const result: any[] = [];
        for (const step of steps) {
            const items = step?.payload?.assets || [];
            for (const asset of items) {
                if (asset?.id && !seen.has(asset.id)) {
                    seen.add(asset.id);
                    result.push(asset);
                }
            }
        }
        return result;
    }, [steps]);

    const hasComplete = steps.some(e => e.completed === true);
    const visibleAssets = assets.slice(0, MAX_VISIBLE_ASSETS);
    const remainingCount = Math.max(0, assets.length - MAX_VISIBLE_ASSETS);

    return (
        <div className={cx(styles.reasoningStep, styles.searchAssetsStep)}>
            <div className={styles.left}>
                {hasComplete ? <div className={styles.stepDot} /> : <div className={styles.spinner} />}
                <div className={styles.line} />
            </div>
            <div className={styles.right}>
                <p className={styles.stepTitle}>{label}</p>
                <div className={styles.stepContent}>
                    {visibleAssets.map((asset: any) => (
                        <a
                            key={asset.id}
                            href={`/assets/${asset.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.assetPill}
                        >
                            <Thumbnail
                                media={asset.icon}
                                width={20}
                                className={cx(styles.assetIcon)}
                                alt=""
                                defaultImage="/images/assets/default_asset.jpg"
                            />
                            <Text
                                className={styles.assetName}
                                text={asset.name || 'Untitled'}
                                lines={1}
                                tooltip
                            />
                        </a>
                    ))}
                    {remainingCount > 0 && (
                        <span className={cx(styles.assetPill, styles.moreAssets)}>
                            + {remainingCount} more assets
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

// Reasoning Answer Step
interface ReasoningAgentStepProps {
    steps: any[];
    label: string;
}

const ReasoningAnswerStep: React.FC<ReasoningAgentStepProps> = ({ steps, label }) => {
    const allComplete = steps.length > 0 && steps.every(e => e.completed !== false);
    return (
        <div className={cx(styles.reasoningStep, styles.craftingStep)}>
            <div className={styles.left}>
                {allComplete ? <div className={styles.stepDot} /> : <i className={cx(styles.spinner, 'fa-solid fa-spinner fa-spin-pulse')} />}
                <div className={styles.line} />
            </div>
            <div className={styles.right}>
                <p className={cx(styles.stepTitle, { [styles.notCompleted]: !allComplete })}>{label}</p>
            </div>
        </div>
    );
};

// Reasoning Critique Step

const ReasoningCritiqueStep: React.FC<ReasoningAgentStepProps> = ({ steps, label }) => {
    const allComplete = steps.length > 0 && steps.every(e => e.completed === true);
    return (
        <div className={cx(styles.reasoningStep, styles.craftingStep)}>
            <div className={styles.left}>
                {allComplete ? <div className={styles.stepDot} /> : <div className={styles.spinner} />}
                <div className={styles.line} />
            </div>
            <div className={styles.right}>
                <p className={styles.stepTitle}>{label}</p>
            </div>
        </div>
    );
};

// Reasoning Response Generated Step

const ReasoningResponseGeneratedStep: React.FC = () => (
    <div className={cx(styles.reasoningStep, styles.craftingStep)}>
        <div className={styles.left}>
            <div className={styles.stepDot} />
            <div className={styles.line} />
        </div>
        <div className={styles.right}>
            <p className={styles.stepTitle}>Response Generated</p>
        </div>
    </div>
);

// FollowupQuestions 
interface FollowupQuestionsProps {
    questions: string[];
    onSelectQuestion: (question: string) => void;
}

const FollowupQuestions: React.FC<FollowupQuestionsProps> = ({
    questions,
    onSelectQuestion,
}) => (
    <div className={styles.followupQuestions}>
        <div className={cx(styles.headerContainer, styles.followupQuestionsHeader)}>
            <p className={styles.followupQuestionsTitle}>Follow up questions</p>
            <div className={styles.separator} />
        </div>
        <div className={styles.followupQuestionsList}>
            {questions.map((question, i) => (
                <button
                    key={i}
                    type="button"
                    className={styles.followupQuestionItem}
                    onClick={() => onSelectQuestion(question)}
                >
                    <i className="fa-solid fa-arrow-turn-up fa-rotate-90" />
                    <span className={styles.followupQuestionText}>{question}</span>
                </button>
            ))}
        </div>
    </div>
);

export default ChatAnswer;
